import { useEffect, useState } from 'preact/hooks';
import { authFetch } from '../api/authFetch';
import { onAuthChange } from '../auth/google-auth';
import {
  useWorkspaceModeEntry,
  reconcile,
  requestMode,
  modeChanged,
  modeChangeFailed,
  dismissModeError,
  openConsent,
  cancelConsent,
  submitConsent,
  consentGranted,
  consentFailed,
  requestRevoke,
  consentRevoked,
  revokeFailed,
  dismissConsentError,
  type WorkspaceStatusWire,
  type ModeChangeResult,
} from '../state/workspaceModeStore';

/**
 * Workspace-mode chooser + own-box consent flow — BYO-box relay (#1062),
 * Phase 2-frontend + Phase 3.
 *
 * PURE RENDERER + WIRE ADAPTER. It reads machine state via `useWorkspaceModeEntry` and
 * writes ONLY through named store actions (R-A2 — no CustomEvent, no local phase state).
 * The `useState` here is confined to the authenticated email (the store's origin key),
 * which is identity, not machine state.
 *
 * Lives in shared-ui/ so both shells can mount it without crossing the mobile/desktop
 * import firewall.
 *
 * WHAT THIS SURFACE CANNOT DO: nothing here reaches a remote box. Selecting `own-box` and
 * accepting consent unlocks the target in `handleSshTargets`, but a REMOTE box is only
 * reachable once the backend host is joined to a tailnet — plan Phase 1, an operator act.
 * Until then this flow is exercisable end-to-end for a LAN box only.
 */
export function WorkspaceModePanel() {
  const [email, setEmail] = useState<string | null>(null);
  const entry = useWorkspaceModeEntry(email);

  useEffect(() => onAuthChange((user) => setEmail(user?.email ?? null)), []);

  // Server-authoritative reconcile on mount / identity change (pattern rule 3).
  useEffect(() => {
    if (!email) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch('/api/workspace');
        if (!res.ok) return;
        const status: WorkspaceStatusWire = await res.json();
        if (!cancelled) reconcile(email, status);
      } catch {
        // A failed status read leaves the phases 'unknown' — the panel renders its
        // loading form rather than guessing a mode.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [email]);

  if (!email) return null;

  const {
    modePhase,
    consentPhase,
    configured,
    host,
    port,
    consentAcceptedAt,
    lastModeError,
    lastConsentError,
  } = entry;

  const busy = modePhase === 'switching';
  const isOwnBox = modePhase === 'own-box';

  async function chooseMode(mode: 'managed' | 'own-box') {
    if (!email || busy || modePhase === mode) return;
    requestMode(email);
    try {
      const res = await authFetch('/api/workspace/mode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        modeChangeFailed(email, body?.message ?? `Could not switch mode (${res.status})`);
        return;
      }
      modeChanged(email, body as ModeChangeResult);
    } catch (e: any) {
      modeChangeFailed(email, e?.message ?? 'Network error');
    }
  }

  async function acceptConsent() {
    if (!email) return;
    submitConsent(email);
    try {
      const res = await authFetch('/api/workspace/consent', { method: 'POST' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        consentFailed(email, body?.message ?? `Could not record consent (${res.status})`);
        return;
      }
      consentGranted(email, { acceptedAt: body.acceptedAt, version: body.version });
    } catch (e: any) {
      consentFailed(email, e?.message ?? 'Network error');
    }
  }

  async function revoke() {
    if (!email) return;
    if (
      !confirm(
        'Revoke access to your machine?\n\nThis removes our SSH key and your saved host/port/user, and switches you back to the managed sandbox.',
      )
    ) {
      return;
    }
    requestRevoke(email);
    try {
      const res = await authFetch('/api/workspace/consent', { method: 'DELETE' });
      if (!res.ok) {
        revokeFailed(email, `Could not revoke (${res.status})`);
        return;
      }
      consentRevoked(email);
    } catch (e: any) {
      revokeFailed(email, e?.message ?? 'Network error');
    }
  }

  return (
    <div class="settings-section">
      <h3>Where your agent runs</h3>

      {modePhase === 'unknown' ? (
        <p class="settings-hint">Loading…</p>
      ) : (
        <div class="ws-mode-options">
          <label class={`ws-mode-option ${modePhase === 'managed' ? 'ws-mode-option--active' : ''}`}>
            <input
              type="radio"
              name="workspace-mode"
              checked={modePhase === 'managed'}
              disabled={busy}
              onChange={() => chooseMode('managed')}
            />
            <span class="ws-mode-label">
              <strong>We host it</strong>
              <span class="settings-hint">A sandbox we provision and maintain. Nothing to set up.</span>
            </span>
          </label>

          <label class={`ws-mode-option ${isOwnBox ? 'ws-mode-option--active' : ''}`}>
            <input
              type="radio"
              name="workspace-mode"
              checked={isOwnBox}
              disabled={busy}
              onChange={() => chooseMode('own-box')}
            />
            <span class="ws-mode-label">
              <strong>Bring my own box</strong>
              <span class="settings-hint">
                Your machine over SSH — on your LAN, or anywhere via Tailscale.
              </span>
            </span>
          </label>
        </div>
      )}

      {lastModeError && (
        <div class="ws-test-result ws-test-fail">
          <strong>Failed</strong> — {lastModeError}
          <button class="settings-btn" style={{ marginLeft: '8px' }} onClick={() => dismissModeError(email!)}>
            Dismiss
          </button>
        </div>
      )}

      {/* ── Own-box readiness: the two outstanding steps, stated plainly ────────── */}
      {isOwnBox && (
        <div class="ws-own-box-state">
          <ul class="ws-checklist">
            <li class={configured ? 'ws-check--done' : 'ws-check--todo'}>
              {configured ? (
                <>
                  Box configured — <code>{host}{port ? `:${port}` : ''}</code>
                </>
              ) : (
                <>No box configured yet — fill in Workspace (BYOM) below.</>
              )}
            </li>
            <li class={consentPhase === 'granted' ? 'ws-check--done' : 'ws-check--todo'}>
              {consentPhase === 'granted' ? (
                <>
                  Access granted
                  {consentAcceptedAt ? ` on ${new Date(consentAcceptedAt).toLocaleDateString()}` : ''}
                </>
              ) : (
                <>Access not granted — your box stays unreachable until you allow it.</>
              )}
            </li>
          </ul>

          {consentPhase === 'absent' && (
            <button class="settings-btn settings-btn--primary" onClick={() => openConsent(email)}>
              Review and allow access
            </button>
          )}
          {consentPhase === 'recording' && <p class="settings-hint">Recording…</p>}
          {consentPhase === 'revoking' && <p class="settings-hint">Revoking…</p>}
          {consentPhase === 'granted' && (
            <button class="settings-btn settings-btn--danger" onClick={revoke}>
              Revoke access
            </button>
          )}
          {lastConsentError && (
            <div class="ws-test-result ws-test-fail">
              <strong>Failed</strong> — {lastConsentError}
              <button
                class="settings-btn"
                style={{ marginLeft: '8px' }}
                onClick={() => dismissConsentError(email)}
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Consent modal ───────────────────────────────────────────────────────── */}
      {consentPhase === 'prompting' && (
        <div class="ws-consent-backdrop" onClick={() => cancelConsent(email)}>
          <div class="ws-consent-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Allow access to your machine</h4>
            <p>
              You are about to let this app connect to <strong>your own computer</strong> over SSH
              to run your agent there.
            </p>
            <ul>
              <li>We connect as the user you configured, using a key we generated for you.</li>
              <li>Your agent runs on that machine and uses the credentials already signed in there.</li>
              <li>
                You can revoke this at any time. Revoking deletes our key and your saved connection
                details, and switches you back to the managed sandbox.
              </li>
            </ul>
            <div class="settings-row" style={{ gap: '8px', marginTop: '12px' }}>
              <button class="settings-btn settings-btn--primary" onClick={acceptConsent}>
                I allow this
              </button>
              <button class="settings-btn" onClick={() => cancelConsent(email)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
